/**
 * 1.3.75: MVP 多租户架构 - 类型选择页面
 * 1.3.78: 添加多语言支持
 * 新用户首次登录后选择个人/企业类型
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { completePersonalOnboarding } from '../services/organizationService';
import { signOut } from '../services/authService';
import { LanguageSwitcher } from '../components/LanguageSwitcher';

interface OnboardingPageProps {
  onComplete?: () => void;
}

export function OnboardingPage({ onComplete }: OnboardingPageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSelectPersonal = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const { result, error: apiError } = await completePersonalOnboarding();
      
      if (apiError) {
        setError(apiError.message);
        return;
      }
      
      // 显示成功提示
      setShowSuccessModal(true);
    } catch (err: any) {
      setError(err.message || t('onboarding.createFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectBusiness = () => {
    navigate('/business-setup');
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    if (onComplete) {
      onComplete();
    } else {
      navigate('/app'); // 1.3.80: 应用主页改为 /app
    }
  };

  // 1.3.79: 返回登录页面
  const handleBackToLogin = async () => {
    await signOut();
    navigate('/auth');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
      {/* 返回按钮 - 左上角 */}
      <button
        onClick={handleBackToLogin}
        className="absolute top-4 left-4 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-all duration-300"
        title={t('onboarding.backToLogin')}
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
      </button>

      {/* 语言切换按钮 - 右上角 */}
      <div className="absolute top-4 right-4">
        <LanguageSwitcher direction="down" />
      </div>
      
      <div className="w-full max-w-3xl">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
            {t('onboarding.welcomeTitle')}
          </h1>
          <p className="mt-4 text-gray-600 text-lg">
            {t('onboarding.selectScenario')}
          </p>
        </div>

        {/* 选择卡片 */}
        <div className="grid md:grid-cols-2 gap-6 items-stretch">
          {/* Individual 个人用户卡片 */}
          <div 
            className="bg-white rounded-2xl shadow-lg p-6 border-2 border-transparent hover:border-indigo-500 hover:shadow-xl transition-all duration-300 cursor-pointer group flex flex-col"
            onClick={handleSelectPersonal}
          >
            {/* 类型标签 */}
            <div className="inline-block px-3 py-1 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full mb-4 self-start">
              Individual
            </div>
            
            <div className="flex items-center gap-3 mb-2">
              {/* 简约扁平风格图标 */}
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-indigo-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{t('onboarding.personalUser')}</h2>
                <p className="text-gray-500 text-xs">{t('onboarding.individualUsers')}</p>
              </div>
            </div>
            
            {/* 价格 */}
            <div className="py-4 border-b border-gray-100 mb-4">
              <div className="flex items-baseline gap-3">
                <span className="text-xl text-gray-400 line-through">¥2,500</span>
                <span className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">¥0</span>
                <span className="text-gray-500 text-sm">/{t('onboarding.perPersonMonth')}</span>
              </div>
              <div className="mt-2">
                <span className="inline-block px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                  {t('onboarding.free7Days')}
                </span>
              </div>
            </div>
            
            {/* 功能列表 - flex-grow 让按钮对齐底部 */}
            <ul className="space-y-2.5 mb-4 text-sm flex-grow">
              <li className="flex items-center text-gray-600">
                <svg className="w-4 h-4 text-indigo-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {t('onboarding.unlimitedChat')}
              </li>
              <li className="flex items-center text-gray-600">
                <svg className="w-4 h-4 text-indigo-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {t('onboarding.publicLink5000')}
              </li>
              <li className="flex items-center text-gray-600">
                <svg className="w-4 h-4 text-indigo-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {t('onboarding.unlimitedKB')}
              </li>
              <li className="flex items-center text-gray-600">
                <svg className="w-4 h-4 text-indigo-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {t('onboarding.storage1GBPerson')}
              </li>
              <li className="flex items-center text-gray-400">
                <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                {t('onboarding.multiUser')}
              </li>
              <li className="flex items-center text-gray-400">
                <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                {t('onboarding.sharedKB')}
              </li>
            </ul>

            {/* 底部区域 - mt-auto 确保对齐底部 */}
            <div className="mt-auto">
              {/* 超额提示 */}
              <p className="text-xs text-gray-400 mb-4">{t('onboarding.overageNote')}</p>

              <button 
              disabled={isLoading}
              className="w-full py-3 px-6 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-medium rounded-xl hover:from-indigo-600 hover:to-purple-600 hover:scale-[1.02] transition-all duration-300 disabled:opacity-50 disabled:hover:scale-100 group-hover:shadow-lg"
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {t('creating')}
                </span>
              ) : t('onboarding.select')}
              </button>
            </div>
          </div>

          {/* Standard 企业用户卡片 */}
          <div 
            className="bg-white rounded-2xl shadow-lg p-6 border-2 border-transparent hover:border-purple-500 hover:shadow-xl transition-all duration-300 cursor-pointer group flex flex-col"
            onClick={handleSelectBusiness}
          >
            {/* 类型标签 */}
            <div className="inline-block px-3 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full mb-4 self-start">
              Standard
            </div>
            
            <div className="flex items-center gap-3 mb-2">
              {/* 简约扁平风格图标 */}
              <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/>
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{t('onboarding.businessUser')}</h2>
                <p className="text-gray-500 text-xs">{t('onboarding.standardUsers')}</p>
              </div>
            </div>
            
            {/* 价格 */}
            <div className="py-4 border-b border-gray-100 mb-4">
              <div className="flex items-baseline gap-3">
                <span className="text-xl text-gray-400 line-through">¥2,300</span>
                <span className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">¥0</span>
                <span className="text-gray-500 text-sm">/{t('onboarding.perPersonMonth')}</span>
              </div>
              <div className="mt-2">
                <span className="inline-block px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                  {t('onboarding.free7Days')}
                </span>
              </div>
            </div>
            
            {/* 功能列表 - flex-grow 让按钮对齐底部 */}
            <ul className="space-y-2.5 mb-4 text-sm flex-grow">
              <li className="flex items-center text-gray-600">
                <svg className="w-4 h-4 text-purple-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {t('onboarding.unlimitedChat')}
              </li>
              <li className="flex items-center text-gray-600">
                <svg className="w-4 h-4 text-purple-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {t('onboarding.publicLink10000')}
              </li>
              <li className="flex items-center text-gray-600">
                <svg className="w-4 h-4 text-purple-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {t('onboarding.unlimitedKB')}
              </li>
              <li className="flex items-center text-gray-600">
                <svg className="w-4 h-4 text-purple-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {t('onboarding.storage10GB')}
              </li>
              <li className="flex items-center text-gray-600">
                <svg className="w-4 h-4 text-purple-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {t('onboarding.multiUser')}
              </li>
              <li className="flex items-center text-gray-600">
                <svg className="w-4 h-4 text-purple-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {t('onboarding.sharedKB')}
              </li>
              <li className="flex items-center text-gray-600">
                <svg className="w-4 h-4 text-purple-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {t('onboarding.rolePermissions')}
              </li>
            </ul>

            {/* 底部区域 - mt-auto 确保对齐底部 */}
            <div className="mt-auto">
              {/* 超额提示 */}
              <p className="text-xs text-gray-400 mb-4">{t('onboarding.overageNote')}</p>

              <button 
                className="w-full py-3 px-6 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium rounded-xl hover:from-purple-600 hover:to-pink-600 hover:scale-[1.02] transition-all duration-300 group-hover:shadow-lg"
              >
                {t('onboarding.select')}
              </button>
            </div>
          </div>
        </div>

        {/* 错误提示 */}
        {error && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-center">
            {error}
          </div>
        )}

        {/* 底部提示 */}
        <p className="text-center text-gray-500 text-sm mt-8">
          {t('onboarding.upgradeAnytime')}
        </p>
      </div>

      {/* 成功弹窗 */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full animate-in fade-in zoom-in duration-300">
            <div className="text-center">
              {/* 简约扁平风格成功图标 */}
              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('onboarding.successTitle')}</h2>
              <p className="text-gray-600 mb-6">
                {t('onboarding.successDesc')}
              </p>
              
              <div className="bg-indigo-50 rounded-xl p-4 mb-6 text-left">
                <h3 className="font-medium text-indigo-900 flex items-center gap-2 mb-2">
                  <svg className="w-5 h-5 text-indigo-500" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 21c0 .55.45 1 1 1h4c.55 0 1-.45 1-1v-1H9v1zm3-19C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7zm2.85 11.1l-.85.6V16h-4v-2.3l-.85-.6A4.997 4.997 0 017 9c0-2.76 2.24-5 5-5s5 2.24 5 5c0 1.63-.8 3.16-2.15 4.1z"/>
                  </svg>
                  {t('onboarding.tipTitle')}
                </h3>
                <p className="text-indigo-700 text-sm">
                  {t('onboarding.tipContent')}
                </p>
              </div>

              <button 
                onClick={handleSuccessClose}
                className="w-full py-3 px-6 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-medium rounded-xl hover:from-indigo-600 hover:to-purple-600 hover:scale-[1.02] transition-all duration-300"
              >
                {t('onboarding.startUsing')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
